import { createStore } from "zustand/vanilla";
import { participantsFromMembers, planCopy, } from "@pack-it-up/core";
import { errorToMessage } from "../errorToMessage.js";
export const createCopyStore = (deps) => {
    const store = createStore((set) => {
        // Every action is read source → create target → apply plan, differing only in which
        // repositories the two ends use. `run` owns the shared busy/error bookkeeping so the
        // three actions read as the plan they build.
        const run = async (steps) => {
            set({ busy: true, error: null });
            const r = await steps();
            set({ busy: false, ...(r.ok ? {} : { error: errorToMessage(r.error) }) });
            return r;
        };
        return {
            busy: false,
            error: null,
            duplicateTemplate: (templateId, name) => run(async () => {
                const source = await deps.templates.fetchTemplateRows(templateId);
                if (!source.ok)
                    return source;
                // The default travelers come along, keyed by the same ids, so the copied
                // rows' `specific` assignments still name them (issue #185).
                const participants = await deps.templates.fetchTemplateParticipants(templateId);
                if (!participants.ok)
                    return participants;
                const created = await deps.templates.createTemplate({ name, participants: participants.value });
                if (!created.ok)
                    return created;
                const plan = planCopy(source.value, {
                    source: { kind: "template" },
                    target: { kind: "template", id: created.value },
                });
                const applied = await deps.applyCopyPlan(plan);
                // The template exists either way; reporting the failure is what matters, and
                // re-running the copy into a fresh template is the recovery.
                if (!applied.ok)
                    return applied;
                return created;
            }),
            createTemplateFromTrip: (trip, name) => run(async () => {
                const source = await deps.trips.fetchTripContents(trip.id);
                if (!source.ok)
                    return source;
                // The trip's members become the template's default travelers, keyed by the
                // same ids (issue #185) — so importing the template back into another trip
                // brings the same people and the copied assignments resolve.
                const sourceTrip = await deps.trips.fetchTrip(trip.id);
                if (!sourceTrip.ok)
                    return sourceTrip;
                const created = await deps.templates.createTemplate({
                    name,
                    ...(sourceTrip.value ? { participants: participantsFromMembers(sourceTrip.value.members) } : {}),
                });
                if (!created.ok)
                    return created;
                const plan = planCopy(source.value, {
                    source: { kind: "trip", ...(trip.startDate !== undefined ? { startDate: trip.startDate } : {}) },
                    target: { kind: "template", id: created.value },
                });
                const applied = await deps.applyCopyPlan(plan);
                if (!applied.ok)
                    return applied;
                return created;
            }),
            copyTrip: (trip, input) => run(async () => {
                const source = await deps.trips.fetchTripContents(trip.id);
                if (!source.ok)
                    return source;
                // The source's members come along so the copied `specific` assignments still
                // name someone (issue #8). `createTrip` overwrites the caller's own entry with
                // an owner entry, so copying a trip you only view still yields a trip you own.
                const sourceTrip = await deps.trips.fetchTrip(trip.id);
                if (!sourceTrip.ok)
                    return sourceTrip;
                const members = sourceTrip.value?.members;
                const created = await deps.trips.createTrip({
                    ...input,
                    ...(members !== undefined ? { members } : {}),
                });
                if (!created.ok)
                    return created;
                const plan = planCopy(source.value, {
                    source: { kind: "trip", ...(trip.startDate !== undefined ? { startDate: trip.startDate } : {}) },
                    target: {
                        kind: "trip",
                        id: created.value,
                        ...(input.startDate !== undefined ? { startDate: input.startDate } : {}),
                    },
                });
                const applied = await deps.applyCopyPlan(plan);
                if (!applied.ok)
                    return applied;
                return created;
            }),
            clearError: () => set({ error: null }),
        };
    });
    return store;
};
// Issue #8 — "only templates I created" is the default toggle on the template lists.
// `TemplateSummary.role` already says who owns what, so no extra read is needed.
export const ownedTemplates = (templates) => templates.filter((t) => t.role === "owner");
//# sourceMappingURL=copyStore.js.map